import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Sparkles, Calendar, Percent, Euro, Play, Pause, Trash2, Plus, AlertCircle, Edit2 } from 'lucide-react';
import { useBodyLock } from '../hooks/useBodyLock';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

const DynamicPromotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);

  // 🔧 FIX MOBILE: Body lock pour les modals
  useBodyLock(showCreateModal || showEditModal);

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/promotions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPromotions(data);
      } else {
        toast.error('Erreur de chargement des promotions');
      }
    } catch (error) {
      console.error('Error fetching promotions:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (promotionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/promotions/${promotionId}/activate`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Clear pricing cache to force refresh on public page
        localStorage.removeItem('pricing_cache');
        localStorage.removeItem('pricing_cache_timestamp');
        
        toast.success('✅ Promotion activée — le tarif public a été mis à jour');
        fetchPromotions();
      } else {
        toast.error('Erreur d\'activation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleDeactivate = async (promotionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/promotions/${promotionId}/deactivate`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Clear pricing cache to force refresh on public page
        localStorage.removeItem('pricing_cache');
        localStorage.removeItem('pricing_cache_timestamp');
        
        toast.success('Promotion désactivée');
        fetchPromotions();
      } else {
        toast.error('Erreur de désactivation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleDelete = async (promotionId) => {
    if (!window.confirm('Supprimer définitivement cette promotion ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/promotions/${promotionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Promotion supprimée');
        fetchPromotions();
      } else {
        toast.error('Erreur de suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleEdit = (promotion) => {
    setEditingPromotion(promotion);
    setShowEditModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const isExpired = (endDate) => {
    if (!endDate) return false;
    return new Date(endDate) < new Date();
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg shadow-lg border-2 border-amber-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-amber-900 flex items-center gap-2">
              <Sparkles className="w-6 h-6" />
              Promotions Dynamiques
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Gérez les promotions affichées automatiquement sur la page d'accueil
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 shadow-md"
          >
            <Plus className="w-5 h-5" />
            Créer Promotion
          </button>
        </div>

        {/* Active Promotion Alert */}
        {promotions.some(p => p.is_active) && (
          <div className="mt-4 bg-green-100 border border-green-400 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-green-700" />
            <span className="text-sm text-green-800 font-medium">
              Une promotion est actuellement active et visible sur la page publique
            </span>
          </div>
        )}
      </div>

      {/* Promotions List */}
      <div className="bg-white rounded-lg shadow">
        {promotions.length === 0 ? (
          <div className="p-12 text-center">
            <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Aucune promotion créée</p>
            <p className="text-sm text-gray-400 mt-2">
              Créez votre première promotion dynamique
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {promotions.map((promo) => (
              <div
                key={promo.id}
                className={`p-6 transition-all ${
                  promo.is_active
                    ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500'
                    : isExpired(promo.end_date)
                    ? 'bg-gray-50 opacity-60'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  {/* Promotion Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-bold text-gray-900">
                        {promo.promotion_name}
                      </h4>
                      {promo.is_active && (
                        <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full animate-pulse">
                          🔥 ACTIF
                        </span>
                      )}
                      {isExpired(promo.end_date) && (
                        <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                          ⏰ EXPIRÉ
                        </span>
                      )}
                    </div>

                    {/* Price Calculation Display */}
                    <div className="bg-white rounded-lg p-4 mb-3 border border-gray-200">
                      <div className="grid grid-cols-3 gap-4 items-center">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">💰 Prix avant</p>
                          <p className="text-2xl font-bold text-gray-700">
                            {promo.original_price.toFixed(2)}€
                          </p>
                        </div>
                        <div className="flex items-center justify-center">
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">⚙️ Réduction</p>
                            <p className="text-xl font-bold text-amber-600">
                              {promo.discount_type === 'percentage' 
                                ? `${promo.discount_value}%`
                                : `${promo.discount_value.toFixed(2)}€`
                              }
                            </p>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">💸 Prix après</p>
                          <p className="text-3xl font-bold text-green-600">
                            {promo.new_price.toFixed(2)}€
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {promo.start_date 
                            ? `Du ${formatDate(promo.start_date)}`
                            : 'Pas de date de début'
                          }
                        </span>
                      </div>
                      <span>→</span>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {promo.end_date 
                            ? `Au ${formatDate(promo.end_date)}`
                            : 'Pas de date de fin'
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(promo)}
                      className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                      title="Modifier cette promotion"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    {!promo.is_active && !isExpired(promo.end_date) && (
                      <button
                        onClick={() => handleActivate(promo.id)}
                        className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                        title="Activer cette promotion"
                      >
                        <Play className="w-5 h-5" />
                      </button>
                    )}
                    {promo.is_active && (
                      <button
                        onClick={() => handleDeactivate(promo.id)}
                        className="p-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors"
                        title="Désactiver cette promotion"
                      >
                        <Pause className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(promo.id)}
                      className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                      title="Supprimer cette promotion"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreatePromotionModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchPromotions();
          }}
        />
      )}
      
      {/* Edit Modal */}
      {showEditModal && editingPromotion && (
        <EditPromotionModal
          promotion={editingPromotion}
          onClose={() => {
            setShowEditModal(false);
            setEditingPromotion(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setEditingPromotion(null);
            fetchPromotions();
          }}
        />
      )}
    </div>
  );
};

// Create Promotion Modal with Live Calculation
const CreatePromotionModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    promotion_name: '',
    discount_type: 'percentage',
    discount_value: '',
    original_price: '29.0',
    duration: 'limited',
    start_date: '',
    end_date: ''
  });
  const [loading, setLoading] = useState(false);

  // Live calculation of new price
  const calculateNewPrice = () => {
    const original = parseFloat(formData.original_price) || 0;
    const discount = parseFloat(formData.discount_value) || 0;

    if (original <= 0 || discount <= 0) return original.toFixed(2);

    if (formData.discount_type === 'percentage') {
      const discountAmount = original * (discount / 100);
      return (original - discountAmount).toFixed(2);
    } else {
      return Math.max(0, original - discount).toFixed(2);
    }
  };

  const newPrice = calculateNewPrice();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.promotion_name || !formData.discount_value || !formData.original_price) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (parseFloat(newPrice) <= 0) {
      toast.error('Le prix après réduction doit être supérieur à 0');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        promotion_name: formData.promotion_name,
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        original_price: parseFloat(formData.original_price),
        duration: formData.duration,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };

      const response = await fetch(`${BACKEND_URL}/api/admin/promotions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('✅ Promotion créée avec succès — le tarif public sera mis à jour si activé');
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating promotion:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-amber-600" />
          Créer une Promotion Dynamique
        </h3>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Promotion Name */}
          <div>
            <label className="block text-sm font-medium mb-1">Nom de la promotion *</label>
            <input
              type="text"
              required
              value={formData.promotion_name}
              onChange={(e) => setFormData({ ...formData, promotion_name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Ex: Offre Lancement (-10%)"
            />
          </div>

          {/* Original Price */}
          <div>
            <label className="block text-sm font-medium mb-1">💰 Prix avant (EUR) *</label>
            <input
              type="number"
              required
              min="0.01"
              step="0.01"
              value={formData.original_price}
              onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="29.00"
            />
          </div>

          {/* Discount Type */}
          <div>
            <label className="block text-sm font-medium mb-1">Type de réduction *</label>
            <select
              value={formData.discount_type}
              onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="percentage">Pourcentage (%)</option>
              <option value="fixed">Montant fixe (€)</option>
            </select>
          </div>

          {/* Discount Value */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {formData.discount_type === 'percentage' ? '% de réduction *' : 'Montant de réduction (EUR) *'}
            </label>
            <input
              type="number"
              required
              min="0.01"
              step={formData.discount_type === 'percentage' ? '1' : '0.01'}
              max={formData.discount_type === 'percentage' ? '100' : undefined}
              value={formData.discount_value}
              onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder={formData.discount_type === 'percentage' ? '20' : '5.00'}
            />
          </div>

          {/* Live Price Calculation Display */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border-2 border-blue-200">
            <p className="text-sm font-medium text-gray-700 mb-3">⚙️ Calcul automatique en temps réel</p>
            <div className="grid grid-cols-3 gap-4 items-center">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Prix avant</p>
                <p className="text-xl font-bold text-gray-700">
                  {parseFloat(formData.original_price || 0).toFixed(2)}€
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Réduction</p>
                <p className="text-lg font-bold text-amber-600">
                  {formData.discount_type === 'percentage'
                    ? `${formData.discount_value || 0}%`
                    : `${parseFloat(formData.discount_value || 0).toFixed(2)}€`
                  }
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">💸 Prix après</p>
                <p className="text-2xl font-bold text-green-600">
                  {newPrice}€
                </p>
              </div>
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium mb-1">Durée</label>
            <select
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="one_time">Une seule fois</option>
              <option value="limited">Limitée (dates de début et fin)</option>
              <option value="permanent">Permanente</option>
            </select>
          </div>

          {/* Dates (only if limited) */}
          {formData.duration === 'limited' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Date de début</label>
                <input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date de fin</label>
                <input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
          )}

          {/* Info Note */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-800">
              ℹ️ <strong>Note:</strong> Une seule promotion peut être active à la fois. 
              Activer cette promotion désactivera automatiquement toutes les autres.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {loading ? 'Création...' : 'Créer la Promotion'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Promotion Modal with Live Calculation
const EditPromotionModal = ({ promotion, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    promotion_name: promotion.promotion_name || '',
    discount_type: promotion.discount_type || 'percentage',
    discount_value: promotion.discount_value?.toString() || '',
    original_price: promotion.original_price?.toString() || '29.0',
    duration: promotion.duration || 'limited',
    start_date: promotion.start_date ? new Date(promotion.start_date).toISOString().slice(0, 16) : '',
    end_date: promotion.end_date ? new Date(promotion.end_date).toISOString().slice(0, 16) : ''
  });
  const [loading, setLoading] = useState(false);

  // Live calculation of new price
  const calculateNewPrice = () => {
    const original = parseFloat(formData.original_price) || 0;
    const discount = parseFloat(formData.discount_value) || 0;

    if (original <= 0 || discount <= 0) return original.toFixed(2);

    if (formData.discount_type === 'percentage') {
      const discountAmount = original * (discount / 100);
      return (original - discountAmount).toFixed(2);
    } else {
      return Math.max(0, original - discount).toFixed(2);
    }
  };

  const newPrice = calculateNewPrice();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.promotion_name || !formData.discount_value || !formData.original_price) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (parseFloat(newPrice) <= 0) {
      toast.error('Le prix après réduction doit être supérieur à 0');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        promotion_name: formData.promotion_name,
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        original_price: parseFloat(formData.original_price),
        duration: formData.duration,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };

      const response = await fetch(`${BACKEND_URL}/api/admin/promotions/${promotion.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('✅ Promotion modifiée avec succès');
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la modification');
      }
    } catch (error) {
      console.error('Error updating promotion:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <Edit2 className="w-6 h-6 text-blue-600" />
          Modifier la Promotion
        </h3>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Promotion Name */}
          <div>
            <label className="block text-sm font-medium mb-1">Nom de la promotion *</label>
            <input
              type="text"
              required
              value={formData.promotion_name}
              onChange={(e) => setFormData({ ...formData, promotion_name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Ex: Offre Lancement (-10%)"
            />
          </div>

          {/* Original Price */}
          <div>
            <label className="block text-sm font-medium mb-1">💰 Prix avant (EUR) *</label>
            <input
              type="number"
              required
              min="0.01"
              step="0.01"
              value={formData.original_price}
              onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="29.00"
            />
          </div>

          {/* Discount Type */}
          <div>
            <label className="block text-sm font-medium mb-1">Type de réduction *</label>
            <select
              value={formData.discount_type}
              onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="percentage">Pourcentage (%)</option>
              <option value="fixed">Montant fixe (€)</option>
            </select>
          </div>

          {/* Discount Value */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {formData.discount_type === 'percentage' ? '% de réduction *' : 'Montant de réduction (EUR) *'}
            </label>
            <input
              type="number"
              required
              min="0.01"
              step={formData.discount_type === 'percentage' ? '1' : '0.01'}
              max={formData.discount_type === 'percentage' ? '100' : undefined}
              value={formData.discount_value}
              onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder={formData.discount_type === 'percentage' ? '20' : '5.00'}
            />
          </div>

          {/* Live Price Calculation Display */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border-2 border-blue-200">
            <p className="text-sm font-medium text-gray-700 mb-3">⚙️ Calcul automatique en temps réel</p>
            <div className="grid grid-cols-3 gap-4 items-center">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Prix avant</p>
                <p className="text-xl font-bold text-gray-700">
                  {parseFloat(formData.original_price || 0).toFixed(2)}€
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Réduction</p>
                <p className="text-lg font-bold text-amber-600">
                  {formData.discount_type === 'percentage'
                    ? `${formData.discount_value || 0}%`
                    : `${parseFloat(formData.discount_value || 0).toFixed(2)}€`
                  }
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">💸 Prix après</p>
                <p className="text-2xl font-bold text-green-600">
                  {newPrice}€
                </p>
              </div>
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium mb-1">Durée</label>
            <select
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="one_time">Une seule fois</option>
              <option value="limited">Limitée (dates de début et fin)</option>
              <option value="permanent">Permanente</option>
            </select>
          </div>

          {/* Dates (only if limited) */}
          {formData.duration === 'limited' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Date de début</label>
                <input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date de fin</label>
                <input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Modification...' : 'Enregistrer les Modifications'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DynamicPromotions;
